import styled from '@emotion/styled';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';

export type SectionTag = 'Validated' | 'Directional' | 'Low confidence';

const BaseSectionChip = styled('span')<{ $bg: string; $color: string }>(({ $bg, $color }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '2px 10px',
  borderRadius: 12,
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '20px',
  backgroundColor: $bg,
  color: $color,
  marginBottom: 12,
  marginTop: 12,
  '&:first-child': {
    marginTop: 0,
  },
}));

const ValidatedIcon = (): React.ReactElement => (
  <svg width="14" height="11" viewBox="0 0 35 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polygon
      fill="currentColor"
      transform="translate(17.5, 13) rotate(-315) translate(-17.5, -13)"
      points="26 -2.75 26 28.75 9 28.75 9 23.75 21 23.75 21 -2.75"
    />
  </svg>
);

const DirectionalIcon =(): React.ReactElement => (
  <svg width="22" height="8" viewBox="0 0 54 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7" cy="7" r="5.25" stroke="currentColor" strokeWidth="3.5" fill="none" />
    <circle cx="27" cy="7" r="5.25" stroke="currentColor" strokeWidth="3.5" fill="none" />
    <circle cx="47" cy="7" r="5.25" stroke="currentColor" strokeWidth="3.5" fill="none" />
    <line x1="11" y1="7" x2="22" y2="7" stroke="currentColor" strokeWidth="4" />
    <line x1="31" y1="7" x2="42" y2="7" stroke="currentColor" strokeWidth="4" />
  </svg>
);

const LowConfidenceIcon = (): React.ReactElement => (
  <svg width="14" height="4" viewBox="0 0 29 6" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="29" height="6" fill="currentColor" />
  </svg>
);

const SECTION_CONFIG: Record<
  SectionTag,
  { bg: string; color: string; label: string; tooltip: string; Icon: () => React.ReactElement }
> = {
  Validated: {
    bg: '#E8F4FB',
    color: '#27AAE1',
    label: 'Validated',
    tooltip: 'Directly supported by evidence from clinical data, research, or cited sources.',
    Icon: ValidatedIcon,
  },
  Directional: {
    bg: '#F0E8F8',
    color: '#7841C9',
    label: 'Directional',
    tooltip: 'Reasoned or inferred rather than directly evidenced; treat as directional guidance.',
    Icon: DirectionalIcon,
  },
  'Low confidence': {
    bg: '#F0F0F0',
    color: '#7F7F7F',
    label: 'Low Confidence',
    tooltip: 'Insufficient evidence or persona basis.',
    Icon: LowConfidenceIcon,
  },
};

export const ValidatedChip = (): React.ReactElement => {
  const { bg, color, label, tooltip, Icon } = SECTION_CONFIG.Validated;
  return (
    <Tooltip title={tooltip}>
      <BaseSectionChip $bg={bg} $color={color}>
        <Icon />
        {label}
      </BaseSectionChip>
    </Tooltip>
  );
};

export const DirectionalChip = (): React.ReactElement => {
  const { bg, color, label, tooltip, Icon } = SECTION_CONFIG.Directional;
  return (
    <Tooltip title={tooltip}>
      <BaseSectionChip $bg={bg} $color={color}>
        <Icon />
        {label}
      </BaseSectionChip>
    </Tooltip>
  );
};

export const LowConfidenceChip = (): React.ReactElement => {
  const { bg, color, label, tooltip, Icon } = SECTION_CONFIG['Low confidence'];
  return (
    <Tooltip title={tooltip}>
      <BaseSectionChip $bg={bg} $color={color}>
        <Icon />
        {label}
      </BaseSectionChip>
    </Tooltip>
  );
};

export const SECTION_CHIPS: Record<SectionTag, () => React.ReactElement> = {
  Validated: ValidatedChip,
  Directional: DirectionalChip,
  'Low confidence': LowConfidenceChip,
};
